import { PRODUCTION_STAFF_APEX } from "@v1/backend/logic/organizations/staff_host";
import { normalizeHostname } from "@v1/backend/logic/portal/portal_host";
import type { ReactElement } from "react";
import {
  CONSENT_MAX_AGE_MS,
  CONSENT_REVISION,
  CONSENT_STORAGE_KEY,
  type ConsentChoices,
} from "./consent-record";

/** What this document already handed to GTM. Head script creates it, client code updates it. */
export interface TagManagerRuntime {
  loaded: boolean;
  statistics: boolean;
  google_ads: boolean;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    hvTagManager?: TagManagerRuntime;
  }
}

export const GTM_CONTAINER_ID = "GTM-5TXRXXC5";

/** GA4 opt-out flag name; stops queued hits flushing on reload after withdrawal. */
const GA4_DISABLE_FLAG = "ga-disable-G-30KENR0K66";

/** Fired when consent arrives after the container already loaded: GTM triggers re-fire blocked tags. */
export const CONSENT_EVENT = {
  statistics: "hv_consent_statistics",
  google_ads: "hv_consent_google_ads",
} as const;

/** Website only: staff app, org hosts, portals, previews stay tag-free. */
const WEBSITE_HOSTNAMES: ReadonlySet<string> = new Set([
  PRODUCTION_STAFF_APEX,
  `www.${PRODUCTION_STAFF_APEX}`,
  "localhost",
  "127.0.0.1",
]);

const WEBSITE_PATH_PREFIXES = [
  "/preise",
  "/demo",
  "/careers",
  "/impressum",
  "/datenschutz",
] as const;

export function isWebsitePath(pathname: string): boolean {
  return (
    pathname === "/" ||
    WEBSITE_PATH_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    )
  );
}

/** Decided per full document load; client navigation keeps the loaded container. */
export function isTagManagerRequest(args: {
  readonly host: string;
  readonly pathname: string | null;
  readonly nativeApp: boolean;
}): boolean {
  const { host, pathname, nativeApp } = args;
  if (nativeApp || pathname === null) return false;
  if (!WEBSITE_HOSTNAMES.has(normalizeHostname(host))) return false;
  return isWebsitePath(pathname);
}

/** Basic Consent Mode v2: everything denied; container itself loads only after a Google consent. */
const CONSENT_DEFAULT = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
} as const;

/** Container may run Google tags only. Meta etc. load from site code with their own switch. */
const TAG_BLOCKLIST: readonly string[] = [
  "customScripts",
  "customPixels",
  "nonGooglePixels",
  "nonGoogleScripts",
  "nonGoogleIframes",
];

/** Statistics → analytics_storage only; Google Ads → the three ad signals only. */
export function googleConsentState(
  choices: Pick<ConsentChoices, "statistics" | "google_ads">,
): Record<keyof typeof CONSENT_DEFAULT, "granted" | "denied"> {
  const ads = choices.google_ads ? "granted" : "denied";
  return {
    analytics_storage: choices.statistics ? "granted" : "denied",
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  };
}

/** Official GTM loader body; `hvTagManager.loaded` makes it run once per document. */
const LOAD_CONTAINER = `if(!window.hvTagManager.loaded){window.hvTagManager.loaded=true;(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer',${JSON.stringify(GTM_CONTAINER_ID)});}`;

/** Mirrors `parseConsentRecord`: stored Google consent reaches GTM before `gtm.js`. */
const RESTORE_STORED_CONSENT = `try{var c=JSON.parse(localStorage.getItem(${JSON.stringify(CONSENT_STORAGE_KEY)})||"null");if(c&&c.revision===${CONSENT_REVISION}&&typeof c.decided_at==="number"&&isFinite(c.decided_at)&&Date.now()-c.decided_at<${CONSENT_MAX_AGE_MS}&&typeof c.statistics==="boolean"&&typeof c.google_ads==="boolean"&&typeof c.meta==="boolean"&&(c.statistics||c.google_ads)){var a=c.google_ads?"granted":"denied";gtag("consent","update",{analytics_storage:c.statistics?"granted":"denied",ad_storage:a,ad_user_data:a,ad_personalization:a});window.hvTagManager.statistics=c.statistics;window.hvTagManager.google_ads=c.google_ads;${LOAD_CONTAINER}}}catch(e){}`;

export const TAG_MANAGER_HEAD_SCRIPT: string = `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.hvTagManager = window.hvTagManager || {loaded:false,statistics:false,google_ads:false};
dataLayer.push({"gtm.blocklist": ${JSON.stringify(TAG_BLOCKLIST)}});
gtag("consent", "default", ${JSON.stringify(CONSENT_DEFAULT)});
${RESTORE_STORED_CONSENT}
`.trim();

/** Plain `<head>` script: runs at parse time. `next/script` would defer to bootstrap. No noscript iframe: no JS, no consent. */
export function GoogleTagManagerHead(): ReactElement {
  return (
    <script
      id="google-tag-manager"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: static snippet, no user input
      dangerouslySetInnerHTML={{ __html: TAG_MANAGER_HEAD_SCRIPT }}
    />
  );
}

/** Custom event for GTM triggers. Never pass form contents or PII. */
export function pushDataLayerEvent(event: string): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ event });
}

function pushGtagCommand(..._args: unknown[]): void {
  window.dataLayer = window.dataLayer ?? [];
  // biome-ignore lint/style/noArguments: GTM only reads consent commands from Arguments objects
  window.dataLayer.push(arguments);
}

function tagManagerRuntime(): TagManagerRuntime {
  window.hvTagManager = window.hvTagManager ?? {
    loaded: false,
    statistics: false,
    google_ads: false,
  };
  return window.hvTagManager;
}

function loadContainer(): void {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script: HTMLScriptElement = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  document.head.appendChild(script);
}

/**
 * Banner decision → Consent Mode. Loads GTM on first Google consent; later
 * grants push one-shot events so blocked tags fire without reload.
 */
export function applyGoogleConsent(choices: ConsentChoices): void {
  if (typeof window === "undefined") return;
  const runtime: TagManagerRuntime = tagManagerRuntime();
  pushGtagCommand("consent", "update", googleConsentState(choices));
  Reflect.set(window, GA4_DISABLE_FLAG, !choices.statistics);
  if (!choices.statistics && !choices.google_ads) return;
  if (!runtime.loaded) {
    runtime.loaded = true;
    runtime.statistics = choices.statistics;
    runtime.google_ads = choices.google_ads;
    loadContainer();
    return;
  }
  if (choices.statistics && !runtime.statistics) {
    runtime.statistics = true;
    pushDataLayerEvent(CONSENT_EVENT.statistics);
  }
  if (choices.google_ads && !runtime.google_ads) {
    runtime.google_ads = true;
    pushDataLayerEvent(CONSENT_EVENT.google_ads);
  }
}

/** True when a Google category got its tags started in this document. */
export function googleCategoryStarted(
  category: "statistics" | "google_ads",
): boolean {
  if (typeof window === "undefined") return false;
  const runtime: TagManagerRuntime = tagManagerRuntime();
  return runtime.loaded && runtime[category];
}

/** Before a forced reload: GA4 would flush a last hit with the new URL. */
export function silenceGoogleAnalytics(): void {
  if (typeof window !== "undefined") Reflect.set(window, GA4_DISABLE_FLAG, true);
}

export function tagManagerLoaded(): boolean {
  return typeof window !== "undefined" && tagManagerRuntime().loaded;
}
